import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import Lottie from "lottie-react";
import Spinner from "../../components/spinner";
import CUSTOMERS from "../../api/services/CUSTOMERS";
import PRODUCTS from "../../api/services/PRODUCTS";
import SERVICES from "../../api/services/SERVICES";
import PROFILE from "../../api/services/PROFILE";
import DOCUMENTS from "../../api/services/DOCUMENTS";
import docOkLottie from "../../assets/lotties/doc-ok.json";
import styles from "../../styles/_screens/cashRegister.module.scss";

// ── helpers ──────────────────────────────────────────────────────────────────
// Standard Israeli VAT rate. The effective rate for *this* business is computed
// in the component once we learn its taxStatus (PATUR / FREELANCER are 0%).
const VAT_RATE_FULL = 0.18;

// Cheap email-shape check used both for inline UI feedback and for the
// submit-time decision to omit invalid emails from the wire payload.
function isEmailish(s) {
  if (!s) return false;
  const t = String(s).trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t);
}

const fmtPrice = (n) => {
  const num = Number(n);
  if (isNaN(num)) return "0.00";
  return num.toFixed(2);
};

const fmtCurrency = (n) =>
  "₪" + (Number(n) || 0).toLocaleString("he-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDateHe = (d) => {
  const date = d instanceof Date ? d : new Date(d);
  return date.toLocaleDateString("he-IL");
};

const DOC_TYPE_LABELS = {
  305: "חשבונית מס-קבלה",
  400: "קבלה",
  320: "חשבונית מס",
  330: "חשבונית זיכוי",
};

const PAYMENT_METHODS = [
  { value: "CREDIT_CARD", label: "אשראי" },
  { value: "CASH", label: "מזומן" },
  { value: "CHECK", label: "המחאה" },
  { value: "BANK_TRANSFER", label: "העברה" },
  { value: "BIT", label: "ביט" },
  { value: "OTHER", label: "אחר" },
];

const CARD_BRANDS = [
  { value: "VISA", label: "ויזה" },
  { value: "MASTERCARD", label: "מאסטרקרד" },
  { value: "AMEX", label: "אמריקן אקספרס" },
  { value: "ISRACARD", label: "ישראכרט" },
  { value: "DINERS", label: "דיינרס" },
  { value: "OTHER", label: "אחר" },
];

const ACQUIRING_BANKS = [
  { value: "CAL", label: "כאל" },
  { value: "ISRACARD", label: "ישראכרט" },
  { value: "MAX", label: "מקס" },
  { value: "ISSUER_OTHER", label: "אחר" },
];

const PAYMENT_TYPES = [
  { value: "REGULAR", label: "רגיל" },
  { value: "INSTALLMENTS", label: "תשלומים" },
  { value: "DEFERRED", label: "נדחה" },
];

const TRANSFER_TYPES = [
  { value: "BIT", label: "ביט" },
  { value: "BANK_TRANSFER", label: "העברה בנקאית" },
  { value: "PEPPER", label: "Pepper" },
  { value: "PAYBOX", label: "Paybox" },
  { value: "PAYPAL", label: "PayPal" },
];

// Build payments-array entry to send to the backend. Strips empty values
// and only emits fields relevant to the selected method.
function buildPaymentLine(p) {
  const base = { method: p.method, amount: Number(p.amount) || 0 };
  if (p.method === "CREDIT_CARD") {
    return {
      ...base,
      ...(p.cardLast4 ? { cardLast4: String(p.cardLast4).slice(0, 4) } : {}),
      ...(p.authCode ? { authCode: p.authCode } : {}),
      ...(p.cardBrand ? { cardBrand: p.cardBrand } : {}),
      ...(p.acquiringBank ? { acquiringBank: p.acquiringBank } : {}),
      ...(p.paymentType ? { paymentType: p.paymentType } : {}),
      ...(p.installments && p.paymentType === "INSTALLMENTS"
        ? { installments: Number(p.installments) }
        : {}),
      ...(p.chargeDate ? { chargeDate: p.chargeDate } : {}),
    };
  }
  if (p.method === "CHECK") {
    return {
      ...base,
      ...(p.checkNumber ? { checkNumber: p.checkNumber } : {}),
      ...(p.dueDate ? { dueDate: p.dueDate } : {}),
      ...(p.bankNumber ? { bankNumber: p.bankNumber } : {}),
      ...(p.branchNumber ? { branchNumber: p.branchNumber } : {}),
      ...(p.accountNumber ? { accountNumber: p.accountNumber } : {}),
    };
  }
  if (p.method === "BIT" || p.method === "BANK_TRANSFER") {
    return {
      ...base,
      ...(p.transferType ? { transferType: p.transferType } : {}),
      ...(p.ref ? { ref: p.ref } : {}),
      ...(p.depositDate ? { depositDate: p.depositDate } : {}),
    };
  }
  if (p.method === "OTHER") {
    return { ...base, ...(p.ref ? { ref: p.ref } : {}) };
  }
  return base; // CASH
}

// Localized one-line description of a payment line for the live preview.
// Mirrors the backend `describePayment` helper so the user sees what
// the PDF will say.
function describePaymentForPreview(p) {
  const labelMap = Object.fromEntries(PAYMENT_METHODS.map((m) => [m.value, m.label]));
  const parts = [labelMap[p.method] || p.method];
  if (p.method === "CREDIT_CARD") {
    const brandLabel = CARD_BRANDS.find((b) => b.value === p.cardBrand)?.label;
    if (brandLabel) parts.push(brandLabel);
    if (p.cardLast4) parts.push(`**** ${p.cardLast4}`);
    if (p.paymentType === "INSTALLMENTS" && p.installments) {
      parts.push(`${p.installments} תשלומים`);
    } else if (p.paymentType) {
      const pt = PAYMENT_TYPES.find((x) => x.value === p.paymentType)?.label;
      if (pt) parts.push(pt);
    }
  } else if (p.method === "CHECK") {
    if (p.checkNumber) parts.push(`מס׳ ${p.checkNumber}`);
    if (p.bankNumber) parts.push(`בנק ${p.bankNumber}`);
    if (p.branchNumber) parts.push(`סניף ${p.branchNumber}`);
  } else if (p.method === "BIT" || p.method === "BANK_TRANSFER") {
    const tt = TRANSFER_TYPES.find((x) => x.value === p.transferType)?.label;
    if (tt) parts.push(tt);
    if (p.ref) parts.push(`אסמכתא ${p.ref}`);
  }
  return parts.join(" / ");
}

let clientSearchTimer = null;

// ─────────────────────────────────────────────────────────────────────────────
export default function PosInvoicing({ onIssued }) {
  // ── Client ──
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [clientSearching, setClientSearching] = useState(false);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  // Inline guest-customer fields kept in component state (no DOM refs).
  const [guestName, setGuestName] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  // "פרטים נוספים" (extra customer fields) — competitor parity. Same three
  // fields work for both the guest flow and a selected-client override
  // (e.g. invoice a known customer under a different legal name for one doc).
  const [guestVatOrTz, setGuestVatOrTz] = useState("");
  const [guestInvoiceName, setGuestInvoiceName] = useState("");
  const [extrasOpen, setExtrasOpen] = useState(false);
  const clientRef = useRef(null);

  // "אפשרויות" popover next to the primary submit button (save-in-debt menu).
  const [optionsOpen, setOptionsOpen] = useState(false);
  const optionsRef = useRef(null);

  // ── Catalog ──
  const [activeTab, setActiveTab] = useState("services");
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);

  // ── Custom item ──
  const [customName, setCustomName] = useState("");
  const [customPrice, setCustomPrice] = useState("");

  // ── Cart ── { id, type, name, unitPrice, quantity, discount, itemId }
  const [cart, setCart] = useState([]);
  const [flashItemId, setFlashItemId] = useState(null);   // catalog button just clicked
  const [flashCartId, setFlashCartId] = useState(null);   // cart row that just received the addition

  // ── Doc type ──
  const [docType, setDocType] = useState(305);

  // ── Payment ──
  const [paymentMethod, setPaymentMethod] = useState("CREDIT_CARD");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentAmountTouched, setPaymentAmountTouched] = useState(false);
  // Method-specific sub-form state (all methods share one bag; we only
  // submit the slice that matches the selected method).
  const [pay, setPay] = useState({
    // CREDIT_CARD
    cardBrand: "VISA",
    cardLast4: "",
    authCode: "",
    paymentType: "REGULAR",
    installments: "",
    acquiringBank: "",
    chargeDate: "",
    // CHECK
    checkNumber: "",
    dueDate: "",
    bankNumber: "",
    branchNumber: "",
    accountNumber: "",
    // BIT / BANK_TRANSFER
    transferType: "",
    ref: "",
    depositDate: "",
  });
  const setPayField = (field, value) => setPay((prev) => ({ ...prev, [field]: value }));

  // When the user switches to a transfer method, default transferType to the
  // matching value so the dropdown isn't blank.
  useEffect(() => {
    if (paymentMethod === "BIT" && !pay.transferType) {
      setPayField("transferType", "BIT");
    } else if (paymentMethod === "BANK_TRANSFER" && !pay.transferType) {
      setPayField("transferType", "BANK_TRANSFER");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod]);

  // ── Note ──
  const [note, setNote] = useState("");

  // ── Business profile (for preview) ──
  const [businessName, setBusinessName] = useState("העסק שלי");
  const [businessVat, setBusinessVat] = useState("");
  const [businessAddress, setBusinessAddress] = useState("");
  const [businessPhone, setBusinessPhone] = useState("");

  // ── Tax status (drives whether the preview shows a VAT line) ──
  // We default to BIG while loading so the preview never spuriously hides VAT
  // for a brand-new mount. Real value comes from DOCUMENTS.get_setup() below.
  const [taxStatus, setTaxStatus] = useState("BIG");

  // ── Submit ──
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // { number, pdfUrl, shortUrl, id }
  const [copied, setCopied] = useState(false);

  // ─────────────────────────────────────────
  //  FETCH BUSINESS PROFILE (for preview header)
  // ─────────────────────────────────────────
  useEffect(() => {
    PROFILE.get()
      .then((res) => {
        const body = res?.data || res;
        const name =
          body?.business_name || body?.businessName || body?.name || body?.user?.name || "";
        if (name) setBusinessName(name);
        setBusinessVat(body?.vat || body?.business_vat || "");
        setBusinessAddress(body?.address || body?.business_address || "");
        setBusinessPhone(body?.phone || body?.business_phone || "");
      })
      .catch(() => {});
  }, []);

  // ─────────────────────────────────────────
  //  FETCH INVOICING SETUP (taxStatus + vat)
  //  We need this so the live preview matches the PDF the backend will emit:
  //  PATUR / FREELANCER → 0% VAT, no VAT row in totals.
  //  Also overrides the businessVat from PROFILE.get() since the invoicing
  //  setup is the canonical source for the VAT number on tax documents.
  // ─────────────────────────────────────────
  useEffect(() => {
    DOCUMENTS.get_setup()
      .then((res) => {
        if (res?.taxStatus) setTaxStatus(res.taxStatus);
        if (res?.vat) setBusinessVat(res.vat);
      })
      .catch(() => {});
  }, []);

  // ─────────────────────────────────────────
  //  FETCH CATALOG DATA ON MOUNT
  // ─────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setCatalogLoading(true);
      try {
        const businessId = JSON.parse(localStorage.getItem("user_id") || '""');
        const [prodRes, svcRes] = await Promise.allSettled([
          PRODUCTS.get_all(),
          SERVICES.get_all({ business_id: businessId }),
        ]);

        if (prodRes.status === "fulfilled") {
          const body = prodRes.value;
          const raw = body?.data || [];
          setProducts(Array.isArray(raw) ? raw.filter((p) => p.active !== false) : []);
        }
        if (svcRes.status === "fulfilled") {
          const body = svcRes.value?.data || svcRes.value;
          const raw = body?.data || [];
          setServices(Array.isArray(raw) ? raw : []);
        }
      } catch (err) {
        console.log("catalog load error", err);
      }
      setCatalogLoading(false);
    };

    load();
  }, []);

  // ─────────────────────────────────────────
  //  CLIENT SEARCH
  // ─────────────────────────────────────────
  const searchClients = useCallback((query) => {
    if (!query || query.length < 2) {
      setClientResults([]);
      setShowClientDropdown(false);
      return;
    }
    clearTimeout(clientSearchTimer);
    clientSearchTimer = setTimeout(async () => {
      setClientSearching(true);
      try {
        const res = await CUSTOMERS.get(1, 10, "all", query);
        const items = res?.data || [];
        setClientResults(Array.isArray(items) ? items : []);
        setShowClientDropdown(true);
      } catch (err) {
        console.log("client search error", err);
        setClientResults([]);
      }
      setClientSearching(false);
    }, 400);
  }, []);

  useEffect(() => {
    searchClients(clientSearch);
  }, [clientSearch, searchClients]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (clientRef.current && !clientRef.current.contains(e.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectClient = (c) => {
    const name = c.user?.name || c.userId?.name || c.name || "";
    const phone = c.user?.phone || c.phone || c.userId?.phone || "";
    const email = c.user?.email || c.email || c.userId?.email || "";
    const userBusinessId = c._id || null;
    setSelectedClient({ ...c, name, phone, email, userBusinessId });
    setClientSearch("");
    setClientResults([]);
    setShowClientDropdown(false);
  };

  const clearClient = () => {
    setSelectedClient(null);
    setClientSearch("");
    setGuestName("");
    setGuestPhone("");
    setGuestEmail("");
    setGuestVatOrTz("");
    setGuestInvoiceName("");
    setExtrasOpen(false);
  };

  // ─────────────────────────────────────────
  //  CART ACTIONS
  // ─────────────────────────────────────────
  const addToCart = (item) => {
    const key = item.id || item._id || `${item.type}-${item.name}`;
    setCart((prev) => {
      const existing = prev.find((c) => c.id === key);
      if (existing) {
        return prev.map((c) => (c.id === key ? { ...c, quantity: c.quantity + 1 } : c));
      }
      return [
        ...prev,
        {
          id: key,
          type: item.type,
          name: item.name,
          unitPrice: Number(item.price) || 0,
          quantity: 1,
          discount: 0,
          itemId: item._id || item.id || null,
        },
      ];
    });
    // Trigger flash on both the catalog button and the cart row.
    const sourceId = item._id || item.id || key;
    setFlashItemId(sourceId);
    setFlashCartId(key);
    setTimeout(() => setFlashItemId((v) => (v === sourceId ? null : v)), 700);
    setTimeout(() => setFlashCartId((v) => (v === key ? null : v)), 700);
  };

  const changeQty = (id, delta) => {
    setCart((prev) =>
      prev
        .map((c) =>
          c.id === id ? { ...c, quantity: Math.max(0, c.quantity + delta) } : c
        )
        .filter((c) => c.quantity > 0)
    );
  };

  const updateItemField = (id, field, value) => {
    setCart((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)));
  };

  const removeFromCart = (id) => {
    setCart((prev) => prev.filter((c) => c.id !== id));
  };

  const addCustomItem = () => {
    if (!customName.trim() || !customPrice) return;
    const price = Number(customPrice);
    if (isNaN(price) || price <= 0) {
      toast.error("נא להזין מחיר תקין");
      return;
    }
    const id = `custom-${Date.now()}`;
    setCart((prev) => [
      ...prev,
      {
        id,
        type: "custom",
        name: customName.trim(),
        unitPrice: price,
        quantity: 1,
        discount: 0,
        itemId: null,
      },
    ]);
    setCustomName("");
    setCustomPrice("");
  };

  // ─────────────────────────────────────────
  //  TOTALS (preview — backend recomputes)
  // ─────────────────────────────────────────
  // BIG / COMPANY → 18% VAT. PATUR / FREELANCER → 0% (no VAT line in preview).
  const vatLiable = taxStatus === "BIG" || taxStatus === "COMPANY";
  const effectiveVatRate = vatLiable ? VAT_RATE_FULL : 0;

  const { subtotal, vat, total } = useMemo(() => {
    const sub = cart.reduce((sum, c) => {
      const line =
        (Number(c.unitPrice) || 0) * (Number(c.quantity) || 0) - (Number(c.discount) || 0);
      return sum + Math.max(0, line);
    }, 0);
    const v = sub * effectiveVatRate;
    return { subtotal: sub, vat: v, total: sub + v };
  }, [cart, effectiveVatRate]);

  // Auto-sync payment amount to total unless the user manually edited it
  useEffect(() => {
    if (!paymentAmountTouched) {
      setPaymentAmount(total > 0 ? total.toFixed(2) : "");
    }
  }, [total, paymentAmountTouched]);

  // ─────────────────────────────────────────
  //  CUSTOMER PAYLOAD BUILDER
  //  Strips empty/invalid fields and merges any "פרטים נוספים" extras
  //  (which apply to BOTH the guest path and the selected-client override
  //  path — same UX as the competitor).
  // ─────────────────────────────────────────
  const buildCustomerPayload = () => {
    let customer = selectedClient;
    if (!customer) {
      const gn = guestName.trim();
      const gp = guestPhone.trim();
      if (!gn) {
        toast.error("נא להזין שם לקוח");
        return null;
      }
      // Phone is mandatory for guest customers — name + phone are the
      // minimum identifying info needed on the document; email / ח.פ /
      // address remain optional under "פרטים נוספים".
      if (!gp) {
        toast.error("נא להזין מספר טלפון של הלקוח");
        return null;
      }
      customer = {
        name: gn,
        phone: gp,
        email: guestEmail.trim(),
        userBusinessId: null,
        isGuest: true,
      };
    }
    const phoneTrimmed = (customer.phone || "").trim();
    const phoneHasDigits = /\d/.test(phoneTrimmed);
    const emailTrimmed = (customer.email || "").trim();
    const emailValid = isEmailish(emailTrimmed);
    const vatTrimmed = (guestVatOrTz || "").trim();
    const invNameTrimmed = (guestInvoiceName || "").trim();
    return {
      name: (customer.name || "לקוח אורח").trim(),
      ...(invNameTrimmed ? { invoiceName: invNameTrimmed } : {}),
      ...(phoneHasDigits ? { phone: phoneTrimmed } : {}),
      ...(emailValid ? { email: emailTrimmed } : {}),
      ...(vatTrimmed ? { vatOrTz: vatTrimmed } : {}),
      ...(customer.userBusinessId ? { userBusinessId: customer.userBusinessId } : {}),
    };
  };

  // ─────────────────────────────────────────
  //  SUBMIT
  //
  //  PRO-gate note: this flow records a document via DOCUMENTS.issue and
  //  does NOT charge a card through any gateway. The `CREDIT_CARD` payment
  //  method here is informational only (it just gets written into the
  //  invoice/receipt). When a real "charge now" toggle is added that calls
  //  the legacy /pos/checkout (or any gateway endpoint), gate that branch
  //  with `useBusinessCapabilities().isPro` and toast
  //  "סליקת אשראי דורשת מנוי PRO פעיל".
  // ─────────────────────────────────────────
  const handleSubmit = async () => {
    const customer = buildCustomerPayload();
    if (!customer) return;

    if (cart.length === 0) {
      toast.error("העגלה ריקה");
      return;
    }
    const amt = Number(paymentAmount) || 0;
    if (docType !== 320 && amt <= 0) {
      toast.error("נא להזין סכום תשלום");
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        docType: Number(docType),
        customer,
        items: cart.map((c) => ({
          name: c.name,
          qty: Number(c.quantity),
          unitPrice: Number(c.unitPrice),
          ...(Number(c.discount) ? { discount: Number(c.discount) } : {}),
        })),
        payments:
          docType === 320
            ? []
            : [buildPaymentLine({ method: paymentMethod, amount: amt, ...pay })],
        ...(note?.trim() ? { note: note.trim() } : {}),
      };

      const result = await DOCUMENTS.issue(body);
      setSuccess({
        number: result?.number || "",
        pdfUrl: result?.pdfUrl || "",
        shortUrl: result?.shortUrl || "",
        id: result?.id || result?._id || "",
      });
      toast.success("המסמך הופק בהצלחה!");
    } catch (err) {
      console.log("issue error", err);
      toast.error(err?.response?.data?.message || "שגיאה בהפקת המסמך");
    }
    setSubmitting(false);
  };

  // ─────────────────────────────────────────
  //  SAVE-IN-DEBT
  //  Two flavours: issueDoc=true → 320 + debt; false → ledger-only.
  //  No payment-amount validation since this flow has no payment.
  // ─────────────────────────────────────────
  const handleSaveDebt = async (issueDoc) => {
    const customer = buildCustomerPayload();
    if (!customer) return;
    if (cart.length === 0) {
      toast.error("העגלה ריקה");
      return;
    }
    if (total <= 0) {
      toast.error("הסכום חייב להיות גדול מ-0");
      return;
    }

    setOptionsOpen(false);
    setSubmitting(true);
    try {
      const body = {
        issueDoc,
        customer,
        items: cart.map((c) => ({
          name: c.name,
          qty: Number(c.quantity),
          unitPrice: Number(c.unitPrice),
          ...(Number(c.discount) ? { discount: Number(c.discount) } : {}),
        })),
        ...(note?.trim() ? { note: note.trim() } : {}),
      };
      const result = await DOCUMENTS.save_debt(body);

      if (issueDoc && result?.pdfUrl) {
        // Open the 320 PDF immediately so the user can hand it to the customer.
        window.open(result.pdfUrl, "_blank", "noopener");
        toast.success(`חשבונית מס׳ ${result.number} הופקה ונשמרה בחוב`);
      } else {
        toast.success("נשמר בחוב");
      }
      resetAll();
    } catch (err) {
      console.log("save-debt error", err);
      toast.error(err?.response?.data?.message || "שגיאה בשמירת החוב");
    }
    setSubmitting(false);
  };

  // Close the options popover on outside click — mirror the client-search
  // dropdown pattern further up.
  useEffect(() => {
    if (!optionsOpen) return;
    const handler = (e) => {
      if (optionsRef.current && !optionsRef.current.contains(e.target)) {
        setOptionsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [optionsOpen]);

  // ─────────────────────────────────────────
  //  RESET
  // ─────────────────────────────────────────
  const resetAll = () => {
    setSelectedClient(null);
    setClientSearch("");
    setGuestName("");
    setGuestPhone("");
    setGuestEmail("");
    setGuestVatOrTz("");
    setGuestInvoiceName("");
    setExtrasOpen(false);
    setOptionsOpen(false);
    setCart([]);
    setPaymentMethod("CREDIT_CARD");
    setPaymentAmount("");
    setPaymentAmountTouched(false);
    setPay({
      cardBrand: "VISA", cardLast4: "", authCode: "",
      paymentType: "REGULAR", installments: "", acquiringBank: "", chargeDate: "",
      checkNumber: "", dueDate: "", bankNumber: "", branchNumber: "", accountNumber: "",
      transferType: "", ref: "", depositDate: "",
    });
    setDocType(305);
    setNote("");
    setSuccess(null);
    setCopied(false);
  };

  // ─────────────────────────────────────────
  //  SHARE HANDLERS (success screen)
  // ─────────────────────────────────────────
  // Prefer the public short URL (stable, evergreen, redirects to a fresh
  // 15-min presigned URL). Fall back to the raw S3 presigned URL only for
  // older docs that were issued before the short-URL feature shipped.
  const shareUrl = success?.shortUrl || success?.pdfUrl || "";

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("העתקה נכשלה");
    }
  };

  const handleEmail = () => {
    if (!success) return;
    const subject = `מסמך מס׳ ${success.number} — ${businessName}`;
    const body = `שלום,\n\nמצורף קישור למסמך מס׳ ${success.number}:\n${shareUrl}\n\nתודה!`;
    const recipient =
      effectiveCustomerEmail && isEmailish(effectiveCustomerEmail)
        ? encodeURIComponent(effectiveCustomerEmail)
        : "";
    window.open(
      `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
      "_blank"
    );
  };

  const handleWhatsApp = () => {
    if (!success) return;
    const text = `מסמך מס׳ ${success.number}\n${shareUrl}`;
    // Strip non-digits; if the number starts with 0 (local IL), convert to +972
    let phone = (effectiveCustomerPhone || "").replace(/\D/g, "");
    if (phone.startsWith("0")) phone = "972" + phone.slice(1);
    const url = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(text)}`
      : `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener");
  };

  // ─────────────────────────────────────────
  //  Customer summary (for sidebar + live preview)
  //  invoiceName + vatOrTz are pulled from the extras inputs regardless of
  //  whether a client is selected — the same "פרטים נוספים" inputs back both
  //  the guest and override flows.
  // ─────────────────────────────────────────
  const effectiveCustomerName = selectedClient?.name || guestName.trim();
  const effectiveCustomerPhone = selectedClient?.phone || guestPhone.trim();
  const effectiveCustomerEmail = selectedClient?.email || guestEmail.trim();
  const effectiveInvoiceName = guestInvoiceName.trim();
  const effectiveVatOrTz = guestVatOrTz.trim();
  // What actually appears on the rendered PDF and preview header. Mirrors the
  // server-side PDF template logic (`invoiceName || name`).
  const displayedCustomerName = effectiveInvoiceName || effectiveCustomerName;

  // ─────────────────────────────────────────
  //  RENDER CATALOG ITEMS (sidebar)
  // ─────────────────────────────────────────
  const renderCatalogItems = () => {
    if (activeTab === "custom") {
      return (
        <div style={ss.customForm}>
          <input
            style={ss.input}
            type="text"
            placeholder="שם פריט"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
          />
          <input
            style={ss.input}
            type="number"
            placeholder="מחיר"
            min="0"
            value={customPrice}
            onChange={(e) => setCustomPrice(e.target.value)}
          />
          <button
            style={{
              ...ss.secondaryBtn,
              opacity: !customName.trim() || !customPrice ? 0.5 : 1,
              cursor: !customName.trim() || !customPrice ? "not-allowed" : "pointer",
            }}
            onClick={addCustomItem}
            disabled={!customName.trim() || !customPrice}
          >
            הוסף לסל
          </button>
        </div>
      );
    }

    let items = [];
    if (activeTab === "products") {
      items = products.map((p) => ({
        _id: p._id,
        type: "product",
        name: p.name || p.title || "",
        price: p.price || 0,
        desc: p.description || "",
      }));
    } else if (activeTab === "services") {
      items = services.map((s) => ({
        _id: s._id,
        type: "service",
        name: s.name || "",
        price: Number(s.price) || 0,
        desc: [
          s.time ? `${s.time} דק'` : "",
          s.type === "CLASS" ? "שיעור" : "",
          s.employeeId?.name || "",
        ]
          .filter(Boolean)
          .join(" · "),
      }));
    }

    if (catalogLoading) {
      return (
        <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
          <Spinner />
        </div>
      );
    }

    if (items.length === 0) {
      return <div style={ss.empty}>אין פריטים להצגה</div>;
    }

    return (
      <div style={ss.catalogList}>
        {items.map((item) => {
          const flashing = flashItemId === item._id;
          return (
            <button
              key={item._id}
              style={{
                ...ss.catalogPick,
                ...(flashing ? ss.catalogPickFlash : {}),
              }}
              onClick={() => addToCart({ ...item, id: item._id })}
              title="הוסף לסל"
            >
              <div style={ss.catalogPickInfo}>
                <div style={ss.catalogPickName}>{item.name}</div>
                {item.desc && <div style={ss.catalogPickDesc}>{item.desc}</div>}
              </div>
              {item.price > 0 && (
                <div style={ss.catalogPickPrice}>{fmtPrice(item.price)} ₪</div>
              )}
              <div style={{ ...ss.catalogPickPlus, ...(flashing ? ss.catalogPickPlusFlash : {}) }}>
                {flashing ? "✓" : "+"}
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  // ─────────────────────────────────────────
  //  RENDER METHOD-SPECIFIC SUB-FORM (sidebar)
  // ─────────────────────────────────────────
  const renderPaymentSubForm = () => {
    if (paymentMethod === "CREDIT_CARD") {
      return (
        <div style={ss.subForm}>
          <div style={ss.formRow}>
            <label style={ss.label}>הגדרת תשלום</label>
            <select
              style={ss.input}
              value={pay.paymentType}
              onChange={(e) => setPayField("paymentType", e.target.value)}
            >
              {PAYMENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          {pay.paymentType === "INSTALLMENTS" && (
            <div style={ss.formRow}>
              <label style={ss.label}>מספר תשלומים</label>
              <input
                style={ss.input}
                type="number"
                min="2"
                max="60"
                value={pay.installments}
                onChange={(e) => setPayField("installments", e.target.value)}
                placeholder="לדוגמה: 3"
              />
            </div>
          )}
          <div style={ss.formRow}>
            <label style={ss.label}>סוג כרטיס</label>
            <select
              style={ss.input}
              value={pay.cardBrand}
              onChange={(e) => setPayField("cardBrand", e.target.value)}
            >
              {CARD_BRANDS.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
          </div>
          <div style={ss.formRowSplit}>
            <div style={{ flex: 1 }}>
              <label style={ss.label}>4 ספרות אחרונות</label>
              <input
                style={ss.input}
                type="text"
                maxLength={4}
                inputMode="numeric"
                value={pay.cardLast4}
                onChange={(e) =>
                  setPayField("cardLast4", e.target.value.replace(/\D/g, "").slice(0, 4))
                }
                placeholder="1234"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={ss.label}>מספר אישור</label>
              <input
                style={ss.input}
                type="text"
                value={pay.authCode}
                onChange={(e) => setPayField("authCode", e.target.value)}
                placeholder="אופציונלי"
              />
            </div>
          </div>
          <div style={ss.formRow}>
            <label style={ss.label}>חברה סולקת</label>
            <select
              style={ss.input}
              value={pay.acquiringBank}
              onChange={(e) => setPayField("acquiringBank", e.target.value)}
            >
              <option value="">— ללא —</option>
              {ACQUIRING_BANKS.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
          </div>
          <div style={ss.formRow}>
            <label style={ss.label}>תאריך חיוב מקורי</label>
            <input
              style={ss.input}
              type="date"
              value={pay.chargeDate ? String(pay.chargeDate).slice(0, 10) : ""}
              onChange={(e) => setPayField("chargeDate", e.target.value)}
            />
          </div>
        </div>
      );
    }

    if (paymentMethod === "CHECK") {
      return (
        <div style={ss.subForm}>
          <div style={ss.formRowSplit}>
            <div style={{ flex: 1 }}>
              <label style={ss.label}>מספר המחאה</label>
              <input
                style={ss.input}
                type="text"
                value={pay.checkNumber}
                onChange={(e) => setPayField("checkNumber", e.target.value)}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={ss.label}>תאריך פירעון</label>
              <input
                style={ss.input}
                type="date"
                value={pay.dueDate ? String(pay.dueDate).slice(0, 10) : ""}
                onChange={(e) => setPayField("dueDate", e.target.value)}
              />
            </div>
          </div>
          <div style={ss.formRowSplit}>
            <div style={{ flex: 1 }}>
              <label style={ss.label}>בנק</label>
              <input
                style={ss.input}
                type="text"
                maxLength={3}
                inputMode="numeric"
                value={pay.bankNumber}
                onChange={(e) =>
                  setPayField("bankNumber", e.target.value.replace(/\D/g, "").slice(0, 3))
                }
                placeholder="לדוגמה: 12"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={ss.label}>סניף</label>
              <input
                style={ss.input}
                type="text"
                maxLength={4}
                inputMode="numeric"
                value={pay.branchNumber}
                onChange={(e) =>
                  setPayField("branchNumber", e.target.value.replace(/\D/g, "").slice(0, 4))
                }
                placeholder="לדוגמה: 482"
              />
            </div>
          </div>
          <div style={ss.formRow}>
            <label style={ss.label}>מספר חשבון</label>
            <input
              style={ss.input}
              type="text"
              value={pay.accountNumber}
              onChange={(e) => setPayField("accountNumber", e.target.value)}
            />
          </div>
        </div>
      );
    }

    if (paymentMethod === "BIT" || paymentMethod === "BANK_TRANSFER") {
      return (
        <div style={ss.subForm}>
          <div style={ss.formRow}>
            <label style={ss.label}>סוג העברה</label>
            <select
              style={ss.input}
              value={pay.transferType}
              onChange={(e) => setPayField("transferType", e.target.value)}
            >
              {TRANSFER_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div style={ss.formRow}>
            <label style={ss.label}>מספר אסמכתא</label>
            <input
              style={ss.input}
              type="text"
              value={pay.ref}
              onChange={(e) => setPayField("ref", e.target.value)}
              placeholder="אופציונלי"
            />
          </div>
          <div style={ss.formRow}>
            <label style={ss.label}>תאריך הפקדה</label>
            <input
              style={ss.input}
              type="date"
              value={pay.depositDate ? String(pay.depositDate).slice(0, 10) : ""}
              onChange={(e) => setPayField("depositDate", e.target.value)}
            />
          </div>
        </div>
      );
    }

    if (paymentMethod === "OTHER") {
      return (
        <div style={ss.subForm}>
          <div style={ss.formRow}>
            <label style={ss.label}>אסמכתא / הערה</label>
            <input
              style={ss.input}
              type="text"
              value={pay.ref}
              onChange={(e) => setPayField("ref", e.target.value)}
              placeholder="פרטים נוספים"
            />
          </div>
        </div>
      );
    }

    // CASH — no extras
    return null;
  };

  // ─────────────────────────────────────────
  //  RENDER LIVE PREVIEW (right pane)
  //  PDF-faithful: white A4 card on light-gray background, Heebo font,
  //  bordered table, totals, payment lines.
  // ─────────────────────────────────────────
  const renderPreview = () => {
    const today = fmtDateHe(new Date());
    const docLabel = DOC_TYPE_LABELS[docType] || "";
    const previewPay =
      docType !== 320 && Number(paymentAmount) > 0
        ? buildPaymentLine({ method: paymentMethod, amount: Number(paymentAmount), ...pay })
        : null;

    return (
      <div style={pv.page} dir="rtl">
        {/* Header */}
        <div style={pv.headerRow}>
          <div style={{ textAlign: "right", flex: 1 }}>
            <div style={pv.bizName}>{businessName}</div>
            {businessVat && <div style={pv.bizMeta}>ע.מ. {businessVat}</div>}
            {businessAddress && <div style={pv.bizMeta}>{businessAddress}</div>}
            {businessPhone && <div style={pv.bizMeta}>טל׳ {businessPhone}</div>}
          </div>
          <div style={{ textAlign: "left", flex: 1 }}>
            <div style={pv.docTitle}>{docLabel}</div>
            <div style={pv.docMeta}>מס׳ (טרם הופק)</div>
            <div style={pv.docMeta}>תאריך {today}</div>
          </div>
        </div>

        <div style={pv.hr} />

        {/* Customer — preview mirrors the backend PDF template logic:
            displayedName = invoiceName || name. If both are set and differ,
            show the system name muted below. */}
        <div>
          <div style={pv.fieldLabel}>לכבוד:</div>
          <div style={pv.customerName}>
            {displayedCustomerName || (
              <span style={pv.placeholder}>(לא נבחר לקוח)</span>
            )}
          </div>
          {effectiveInvoiceName &&
            effectiveCustomerName &&
            effectiveInvoiceName !== effectiveCustomerName && (
              <div style={{ ...pv.customerMeta, color: "#9CA3AF" }}>
                ({effectiveCustomerName})
              </div>
            )}
          {effectiveVatOrTz && (
            <div style={pv.customerMeta}>ח.פ / ת.ז: {effectiveVatOrTz}</div>
          )}
          {effectiveCustomerPhone && (
            <div style={pv.customerMeta}>{effectiveCustomerPhone}</div>
          )}
          {effectiveCustomerEmail && (
            <div style={pv.customerMeta}>{effectiveCustomerEmail}</div>
          )}
        </div>

        <div style={pv.hr} />

        {/* Items table */}
        <table style={pv.table}>
          <thead>
            <tr style={pv.tableHead}>
              <th style={{ ...pv.th, width: "40%" }}>תיאור</th>
              <th style={{ ...pv.th, width: "10%", textAlign: "center" }}>כמות</th>
              <th style={{ ...pv.th, width: "15%" }}>מחיר</th>
              <th style={{ ...pv.th, width: "10%" }}>הנחה</th>
              <th style={{ ...pv.th, width: "25%" }}>סה״כ</th>
            </tr>
          </thead>
          <tbody>
            {cart.length === 0 ? (
              <tr>
                <td colSpan={5} style={pv.tableEmpty}>
                  (לא נוספו פריטים)
                </td>
              </tr>
            ) : (
              cart.map((c) => {
                const lineTotal = Math.max(
                  0,
                  (Number(c.unitPrice) || 0) * (Number(c.quantity) || 0) -
                    (Number(c.discount) || 0)
                );
                return (
                  <tr key={c.id} style={pv.tableRow}>
                    <td style={pv.td}>{c.name}</td>
                    <td style={{ ...pv.td, textAlign: "center" }}>{c.quantity}</td>
                    <td style={pv.td}>{fmtCurrency(c.unitPrice)}</td>
                    <td style={pv.td}>{c.discount ? fmtCurrency(c.discount) : "—"}</td>
                    <td style={{ ...pv.td, fontWeight: 700 }}>{fmtCurrency(lineTotal)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        <div style={pv.hr} />

        {/* Totals — keep this in sync with the PDF templates' totalsLines.
            PATUR / FREELANCER (vatLiable=false) → skip the VAT row entirely. */}
        <div style={pv.totalsBlock}>
          <div style={pv.totalLine}>
            <span>סכום ביניים:</span>
            <span>{fmtCurrency(subtotal)}</span>
          </div>
          {vatLiable && (
            <div style={pv.totalLine}>
              <span>מע״מ 18%:</span>
              <span>{fmtCurrency(vat)}</span>
            </div>
          )}
          <div style={pv.totalGrand}>
            <span>סה״כ לתשלום:</span>
            <span>{fmtCurrency(total)}</span>
          </div>
        </div>

        {/* Payment */}
        {previewPay && (
          <>
            <div style={pv.hr} />
            <table style={pv.table}>
              <thead>
                <tr style={pv.tableHead}>
                  <th style={{ ...pv.th, width: "55%" }}>אמצעי תשלום</th>
                  <th style={{ ...pv.th, width: "20%" }}>אסמכתא</th>
                  <th style={{ ...pv.th, width: "25%" }}>סכום</th>
                </tr>
              </thead>
              <tbody>
                <tr style={pv.tableRow}>
                  <td style={pv.td}>
                    {describePaymentForPreview({
                      method: paymentMethod,
                      ...pay,
                    })}
                  </td>
                  <td style={pv.td}>{pay.authCode || pay.ref || pay.checkNumber || "—"}</td>
                  <td style={pv.td}>{fmtCurrency(Number(paymentAmount) || 0)}</td>
                </tr>
              </tbody>
            </table>
          </>
        )}

        {note?.trim() && (
          <>
            <div style={pv.hr} />
            <div>
              <div style={pv.fieldLabel}>הערה:</div>
              <div style={pv.noteText}>{note}</div>
            </div>
          </>
        )}

        <div style={pv.footer}>מסמך זה הופק על ידי תוכנה רשומה ברשות המסים</div>
      </div>
    );
  };

  // ─────────────────────────────────────────
  //  RENDER
  //
  //  Layout: left sidebar (380px) + right preview pane (fills rest).
  //  In RTL, the first grid column is on the RIGHT. To get the sidebar
  //  visually on the LEFT, we set the grid container to `dir="ltr"`
  //  and re-apply `dir="rtl"` inside each cell.
  // ─────────────────────────────────────────
  // ─────────────────────────────────────────
  //  SUCCESS LAYOUT — replaces both columns after a doc is issued.
  //  Left sidebar shows doc # + share buttons; right pane shows Lottie.
  //  Renders inline (still inside the same grid) so the parent's
  //  doc-tabs header ("קופה" / "מסמכים שהופקו") stays visible.
  // ─────────────────────────────────────────
  if (success) {
    return (
      <div className={styles.container}>
        <div style={ll.shell} dir="ltr">
          {/* LEFT — celebratory Lottie (rendered first so it takes the 1fr column) */}
          <main
            style={{
              ...ll.previewPane,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: 32,
            }}
            dir="rtl"
          >
            <div style={{ width: 480, maxWidth: "100%" }}>
              <Lottie animationData={docOkLottie} loop={false} autoplay={true} />
            </div>
            <div
              style={{
                marginTop: 24,
                fontSize: 30,
                fontWeight: 800,
                color: "#065F46",
                fontFamily: "Heebo, sans-serif",
              }}
            >
              ✓ המסמך הופק בהצלחה
            </div>
            <div
              style={{
                marginTop: 8,
                fontSize: 15,
                color: "#6B7280",
                fontFamily: "Heebo, sans-serif",
              }}
            >
              מסמך מס׳ {success.number} • {DOC_TYPE_LABELS[docType] || docType}
            </div>
          </main>

          {/* RIGHT — success info + share */}
          <aside style={ll.sidebar} dir="rtl">
            <h2 style={ll.sidebarTitle}>קופה — מסמך הופק ✓</h2>

            <div
              style={{
                marginTop: 4,
                padding: 16,
                background: "#F0FDF4",
                border: "1px solid #BBF7D0",
                borderRadius: 12,
              }}
            >
              <div style={{ fontSize: 12, color: "#15803D", marginBottom: 4 }}>
                מספר מסמך
              </div>
              <div style={{ fontSize: 24, fontWeight: 800, color: "#065F46" }}>
                #{success.number}
              </div>
              <div style={{ fontSize: 12, color: "#15803D", marginTop: 6 }}>
                {DOC_TYPE_LABELS[docType] || docType}
              </div>
            </div>

            {shareUrl && (
              <div style={{ marginTop: 20 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#374151",
                    marginBottom: 8,
                  }}
                >
                  קישור למסמך
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  <input
                    readOnly
                    value={shareUrl}
                    style={{
                      flex: 1,
                      padding: "8px 10px",
                      border: "1px solid #E5E7EB",
                      borderRadius: 8,
                      fontSize: 11,
                      fontFamily: "monospace",
                      direction: "ltr",
                      textAlign: "left",
                    }}
                    onClick={(e) => e.target.select()}
                  />
                </div>
                {success.shortUrl && (
                  <div
                    style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}
                  >
                    קישור קצוב — מפנה למסמך המקורי בכל פעם
                  </div>
                )}
                <a
                  href={shareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "block",
                    marginTop: 8,
                    fontSize: 13,
                    color: "#E93D67",
                    fontWeight: 700,
                    textDecoration: "underline",
                  }}
                >
                  📄 פתח את המסמך בלשונית חדשה
                </a>
              </div>
            )}

            <div style={{ marginTop: 24 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#374151",
                  marginBottom: 8,
                }}
              >
                שיתוף
              </div>

              <button onClick={handleCopy} style={shareBtnStyle}>
                <span>📋</span> {copied ? "✓ הקישור הועתק" : "העתק קישור"}
              </button>

              <button onClick={handleEmail} style={shareBtnStyle}>
                <span>✉️</span> שלח באימייל
              </button>

              <button
                onClick={handleWhatsApp}
                style={{
                  ...shareBtnStyle,
                  background: "#25D366",
                  color: "#fff",
                  border: "none",
                }}
              >
                <span>📱</span> שלח בוואטסאפ
              </button>
            </div>

            <button
              onClick={resetAll}
              style={{
                ...ll.submitBtn,
                marginTop: 24,
                background: "#fff",
                color: "#E93D67",
                border: "2px solid #E93D67",
                boxShadow: "none",
              }}
            >
              ➕ הפק מסמך נוסף
            </button>

            {onIssued && (
              <button
                style={{
                  marginTop: 12,
                  width: "100%",
                  background: "transparent",
                  border: "none",
                  color: "#6B7280",
                  fontSize: 14,
                  fontFamily: "inherit",
                  cursor: "pointer",
                  padding: "8px 0",
                }}
                onClick={() => {
                  setSuccess(null);
                  onIssued();
                }}
              >
                לרשימת המסמכים
              </button>
            )}
          </aside>

        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div style={ll.shell} dir="ltr">
        {/* ─── LEFT PREVIEW PANE ─────────────────────────── */}
        <main style={ll.previewPane} dir="rtl">
          {/* Sticky banner — keeps the preview-only status visible even
              after scrolling down a long document. */}
          <div style={ll.previewBanner}>
            <span style={ll.previewBannerIcon}>👁</span>
            <span>תצוגה מקדימה בלבד — המסמך לא הופק עדיין</span>
          </div>
          {/* Diagonal watermark over the document body. Pointer-events:none
              so the seller can still scroll / click through it. */}
          <div style={ll.previewWatermarkWrap}>
            <div style={ll.previewWatermarkInner}>
              <div style={ll.previewWatermarkText}>תצוגה מקדימה</div>
            </div>
            {renderPreview()}
          </div>
        </main>

        {/* ─── RIGHT SIDEBAR ─────────────────────────── */}
        <aside style={ll.sidebar} dir="rtl">
          <h2 style={ll.sidebarTitle}>קופה — הפקת מסמך</h2>

          {/* 1. DOC TYPE */}
          <section style={ll.section}>
            <div style={ll.sectionHeading}>סוג מסמך</div>
            <select
              style={ll.docTypeSelect}
              value={docType}
              onChange={(e) => setDocType(Number(e.target.value))}
            >
              <option value={305}>חשבונית מס-קבלה (305)</option>
              <option value={400}>קבלה (400)</option>
              <option value={320}>חשבונית מס (320)</option>
            </select>
          </section>

          {/* 2. CUSTOMER */}
          <section style={ll.section}>
            <div style={ll.sectionHeading}>לקוח</div>
            {selectedClient ? (
              <>
                <div style={ll.clientChipRow}>
                  <div style={ll.clientChipName}>
                    {selectedClient.name}
                    {selectedClient.phone && (
                      <span style={ll.clientChipMeta}> · {selectedClient.phone}</span>
                    )}
                  </div>
                  <button style={ll.clientClear} onClick={clearClient}>
                    ×
                  </button>
                </div>
                {/* Even for a known customer, allow per-document overrides:
                    invoice them under a different legal name (TLS Ltd vs
                    "תל"), or attach a ח.פ for this doc only. */}
                <button
                  type="button"
                  style={ll.extrasToggle}
                  onClick={() => setExtrasOpen((v) => !v)}
                >
                  {extrasOpen ? "▴" : "▾"} פרטים נוספים
                </button>
                {extrasOpen && (
                  <div style={ll.extrasBox}>
                    <input
                      style={ss.input}
                      type="text"
                      placeholder="ח.פ / ת.ז (אופציונלי)"
                      value={guestVatOrTz}
                      onChange={(e) => setGuestVatOrTz(e.target.value)}
                    />
                    <input
                      style={ss.input}
                      type="text"
                      placeholder="שם לחשבונית (אם שונה מהשם השמור)"
                      value={guestInvoiceName}
                      onChange={(e) => setGuestInvoiceName(e.target.value)}
                    />
                  </div>
                )}
              </>
            ) : (
              <div ref={clientRef} style={{ position: "relative" }}>
                <input
                  style={ss.input}
                  type="text"
                  placeholder="חיפוש לפי שם או טלפון..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  onFocus={() => {
                    if (clientResults.length > 0) setShowClientDropdown(true);
                  }}
                />
                {clientSearching && (
                  <div style={{ position: "absolute", top: 8, left: 8 }}>
                    <Spinner />
                  </div>
                )}
                {showClientDropdown && clientResults.length > 0 && (
                  <div style={ll.dropdown}>
                    {clientResults.map((c) => {
                      const name = c.user?.name || c.userId?.name || c.name || "";
                      const phone = c.user?.phone || c.phone || "";
                      return (
                        <button
                          key={c._id}
                          style={ll.dropdownOption}
                          onClick={() => selectClient(c)}
                        >
                          <span>{name}</span>
                          {phone && <span style={ll.dropdownPhone}>{phone}</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div style={ll.guestBox}>
                  <div style={ll.guestLabel}>או הזן פרטי לקוח אורח:</div>
                  {/* Always-visible: name + phone. */}
                  <input
                    style={ss.input}
                    type="text"
                    placeholder="שם לקוח *"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                  />
                  <input
                    style={ss.input}
                    type="tel"
                    placeholder="טלפון *"
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                  />
                  {/* Collapsed extras — email + ח.פ + שם לחשבונית.
                      Match the competitor's "פרטים נוספים" toggle. */}
                  <button
                    type="button"
                    style={ll.extrasToggle}
                    onClick={() => setExtrasOpen((v) => !v)}
                  >
                    {extrasOpen ? "▴" : "▾"} פרטים נוספים
                  </button>
                  {extrasOpen && (
                    <div style={ll.extrasBox}>
                      <input
                        style={ss.input}
                        type="text"
                        placeholder="אימייל (אופציונלי)"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                      />
                      <input
                        style={ss.input}
                        type="text"
                        placeholder="ח.פ / ת.ז (אופציונלי)"
                        value={guestVatOrTz}
                        onChange={(e) => setGuestVatOrTz(e.target.value)}
                      />
                      <input
                        style={ss.input}
                        type="text"
                        placeholder="שם לחשבונית (אם שונה מהשם הרגיל)"
                        value={guestInvoiceName}
                        onChange={(e) => setGuestInvoiceName(e.target.value)}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>

          {/* 3. ITEMS */}
          <section style={ll.section}>
            <div style={ll.sectionHeading}>פריטים</div>
            <div style={ll.catalogTabs}>
              {[
                { key: "services", label: "שירותים" },
                { key: "products", label: "מוצרים" },
                { key: "custom", label: "פריט חופשי" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  style={{
                    ...ll.catalogTab,
                    ...(activeTab === tab.key ? ll.catalogTabActive : {}),
                  }}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div>{renderCatalogItems()}</div>

            {/* Cart list */}
            {cart.length > 0 && (
              <div style={ll.cartList}>
                <div style={ll.cartListHeading}>בסל ({cart.length})</div>
                {cart.map((item) => {
                  const lineTotal = Math.max(
                    0,
                    (Number(item.unitPrice) || 0) * (Number(item.quantity) || 0) -
                      (Number(item.discount) || 0)
                  );
                  const flashing = flashCartId === item.id;
                  return (
                    <div key={item.id} style={{ ...ll.cartRow, ...(flashing ? ll.cartRowFlash : {}) }}>
                      <div style={ll.cartRowTop}>
                        <div style={ll.cartName}>{item.name}</div>
                        <button
                          style={ll.cartRemove}
                          onClick={() => removeFromCart(item.id)}
                        >
                          ×
                        </button>
                      </div>
                      <div style={ll.cartControls}>
                        <div style={ll.qtyGroup}>
                          <button
                            style={ll.qtyBtn}
                            onClick={() => changeQty(item.id, -1)}
                          >
                            −
                          </button>
                          <span style={ll.qtyVal}>{item.quantity}</span>
                          <button
                            style={ll.qtyBtn}
                            onClick={() => changeQty(item.id, 1)}
                          >
                            +
                          </button>
                        </div>
                        <input
                          style={ll.miniInput}
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unitPrice}
                          onChange={(e) =>
                            updateItemField(item.id, "unitPrice", e.target.value)
                          }
                          title="מחיר"
                        />
                        <input
                          style={ll.miniInput}
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.discount}
                          onChange={(e) =>
                            updateItemField(item.id, "discount", e.target.value)
                          }
                          title="הנחה"
                        />
                        <div style={ll.cartLineTotal}>{fmtPrice(lineTotal)} ₪</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* 4. PAYMENT */}
          {docType !== 320 && (
            <section style={ll.section}>
              <div style={ll.sectionHeading}>תשלום</div>
              <div style={ll.methodRow}>
                {PAYMENT_METHODS.map((m) => (
                  <button
                    key={m.value}
                    style={{
                      ...ll.methodChip,
                      ...(paymentMethod === m.value ? ll.methodChipActive : {}),
                    }}
                    onClick={() => setPaymentMethod(m.value)}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              {renderPaymentSubForm()}
              <div style={ss.formRow}>
                <label style={ss.label}>סכום</label>
                <input
                  style={ss.input}
                  type="number"
                  min="0"
                  step="0.01"
                  value={paymentAmount}
                  onChange={(e) => {
                    setPaymentAmount(e.target.value);
                    setPaymentAmountTouched(true);
                  }}
                  placeholder="0.00"
                />
              </div>
            </section>
          )}

          {/* 5. NOTE */}
          <section style={ll.section}>
            <div style={ll.sectionHeading}>הערה (אופציונלי)</div>
            <input
              style={ss.input}
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="הערה למסמך"
            />
          </section>

          {/* SUBMIT — primary "issue" button + secondary "options" popover
              for the two save-in-debt flows. */}
          <div style={ll.submitBar}>
            <div style={ll.submitRow}>
              <button
                style={{
                  ...ll.submitBtn,
                  ...ll.submitBtnFlex,
                  opacity: cart.length === 0 || submitting ? 0.6 : 1,
                  cursor: cart.length === 0 || submitting ? "not-allowed" : "pointer",
                }}
                disabled={cart.length === 0 || submitting}
                onClick={handleSubmit}
              >
                {submitting
                  ? "מפיק..."
                  : docType === 320
                  ? `הפק חשבונית ${fmtPrice(total)} ₪`
                  : `הפק מסמך ${fmtPrice(total)} ₪`}
              </button>
              <div ref={optionsRef} style={{ position: "relative" }}>
                <button
                  type="button"
                  style={{
                    ...ll.optionsBtn,
                    opacity: cart.length === 0 || submitting ? 0.5 : 1,
                    cursor: cart.length === 0 || submitting ? "not-allowed" : "pointer",
                  }}
                  disabled={cart.length === 0 || submitting}
                  onClick={() => setOptionsOpen((v) => !v)}
                >
                  אפשרויות ▾
                </button>
                {optionsOpen && (
                  <div style={ll.optionsMenu}>
                    <button
                      style={ll.optionsItem}
                      onClick={() => handleSaveDebt(true)}
                    >
                      הפק חשבונית + שמירה בחוב
                    </button>
                    <button
                      style={ll.optionsItem}
                      onClick={() => handleSaveDebt(false)}
                    >
                      שמירה ללא מסמך
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

// ─── Inline styles ────────────────────────────────────────────────────────────
// Outer layout: sidebar + preview pane.
// The grid container is dir="ltr" so the FIRST grid column lands on the LEFT
// (the sidebar). Each cell re-applies dir="rtl" for Hebrew content order.

// Shared style for the three share buttons on the success screen.
const shareBtnStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  width: "100%",
  padding: "12px 14px",
  marginBottom: 8,
  background: "#F9FAFB",
  border: "1px solid #E5E7EB",
  borderRadius: 10,
  cursor: "pointer",
  fontSize: 14,
  fontWeight: 600,
  color: "#111827",
  textAlign: "right",
  fontFamily: "inherit",
  transition: "background 0.15s",
};

const ll = {
  shell: {
    display: "grid",
    // Preview pane on the LEFT, sidebar on the RIGHT (in RTL flow this means
    // the first column visually lands on the right).
    gridTemplateColumns: "1fr 400px",
    width: "100%",
    height: "calc(100vh - 120px)",
    minHeight: 700,
    background: "#F3F4F6",
    overflow: "hidden",
  },
  sidebar: {
    background: "#FFFFFF",
    borderLeft: "1px solid #E5E7EB",
    overflowY: "auto",
    padding: "22px 22px 100px 22px",
    position: "relative",
    fontFamily: "Heebo, Arial, sans-serif",
    fontSize: 15,
  },
  sidebarTitle: {
    fontSize: 20,
    fontWeight: 800,
    color: "#111827",
    margin: "0 0 18px 0",
  },
  section: {
    marginBottom: 24,
  },
  sectionHeading: {
    fontSize: 13,
    fontWeight: 700,
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 10,
  },
  docTypeSelect: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: 10,
    border: "1.5px solid #E5E7EB",
    background: "#fff",
    color: "#111827",
    fontSize: 15,
    fontWeight: 600,
    fontFamily: "inherit",
    cursor: "pointer",
  },
  docTypeRow: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  docTypeBtn: {
    padding: "10px 12px",
    borderRadius: 10,
    border: "1.5px solid #E5E7EB",
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
    textAlign: "right",
    transition: "all 120ms",
  },
  docTypeBtnActive: {
    background: "#FCE7F3",
    borderColor: "#E93D67",
    color: "#9D174D",
  },
  clientChipRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#FCE7F3",
    color: "#9D174D",
    padding: "10px 14px",
    borderRadius: 10,
    border: "1.5px solid #FBCFE8",
  },
  clientChipName: { fontSize: 13, fontWeight: 700 },
  clientChipMeta: { fontWeight: 400, color: "#831843" },
  clientClear: {
    background: "transparent",
    border: "none",
    color: "#9D174D",
    fontSize: 18,
    cursor: "pointer",
    padding: 0,
    width: 24,
    height: 24,
  },
  dropdown: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    marginTop: 4,
    background: "#fff",
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    zIndex: 20,
    maxHeight: 260,
    overflowY: "auto",
  },
  dropdownOption: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    padding: "10px 12px",
    background: "#fff",
    border: "none",
    borderBottom: "1px solid #F3F4F6",
    color: "#111827",
    fontSize: 13,
    fontFamily: "inherit",
    cursor: "pointer",
    textAlign: "right",
  },
  dropdownPhone: { color: "#9CA3AF", fontSize: 12 },
  guestBox: {
    marginTop: 12,
    paddingTop: 12,
    borderTop: "1px dashed #E5E7EB",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  guestLabel: {
    fontSize: 11,
    color: "#6B7280",
    fontWeight: 600,
    marginBottom: 2,
  },
  extrasToggle: {
    background: "transparent",
    border: "none",
    color: "#E93D67",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    padding: "4px 0",
    textAlign: "right",
    fontFamily: "inherit",
  },
  extrasBox: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    paddingTop: 8,
    borderTop: "1px dashed #E5E7EB",
    marginTop: 4,
  },
  catalogTabs: {
    display: "flex",
    gap: 4,
    background: "#F3F4F6",
    padding: 4,
    borderRadius: 10,
    marginBottom: 10,
  },
  catalogTab: {
    flex: 1,
    padding: "8px 10px",
    borderRadius: 8,
    border: "none",
    background: "transparent",
    color: "#6B7280",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  catalogTabActive: {
    background: "#fff",
    color: "#111827",
    boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
  },
  cartList: {
    marginTop: 14,
    background: "#F9FAFB",
    borderRadius: 10,
    padding: 10,
    border: "1px solid #E5E7EB",
  },
  cartListHeading: {
    fontSize: 11,
    fontWeight: 700,
    color: "#6B7280",
    marginBottom: 8,
    textTransform: "uppercase",
  },
  cartRow: {
    padding: "10px 8px",
    borderBottom: "1px solid #E5E7EB",
    borderRadius: 8,
    transition: "background 350ms ease, box-shadow 350ms ease",
  },
  cartRowFlash: {
    background: "#DCFCE7",
    boxShadow: "inset 0 0 0 1.5px #22C55E",
  },
  cartRowTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  cartName: {
    fontSize: 14,
    fontWeight: 600,
    color: "#111827",
    flex: 1,
  },
  cartRemove: {
    background: "transparent",
    border: "none",
    color: "#9CA3AF",
    fontSize: 18,
    cursor: "pointer",
    padding: "0 4px",
  },
  cartControls: {
    display: "flex",
    gap: 6,
    alignItems: "center",
    flexWrap: "wrap",
  },
  qtyGroup: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  qtyBtn: {
    width: 24,
    height: 24,
    borderRadius: 6,
    border: "1px solid #E5E7EB",
    background: "#fff",
    color: "#374151",
    fontSize: 14,
    cursor: "pointer",
    padding: 0,
    fontFamily: "inherit",
  },
  qtyVal: {
    fontSize: 13,
    fontWeight: 600,
    minWidth: 18,
    textAlign: "center",
  },
  miniInput: {
    width: 56,
    height: 28,
    border: "1px solid #E5E7EB",
    borderRadius: 6,
    padding: "0 6px",
    fontSize: 12,
    color: "#111827",
    fontFamily: "inherit",
    outline: "none",
    direction: "ltr",
    textAlign: "center",
  },
  cartLineTotal: {
    marginInlineStart: "auto",
    fontSize: 13,
    fontWeight: 700,
    color: "#111827",
  },
  methodRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginBottom: 12,
  },
  methodChip: {
    padding: "6px 12px",
    borderRadius: 16,
    border: "1.5px solid #E5E7EB",
    background: "#fff",
    color: "#374151",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  methodChipActive: {
    background: "#E93D67",
    borderColor: "#E93D67",
    color: "#fff",
  },
  submitBar: {
    // In-flow, not floating. A solid top border separates it from the
    // controls above without the lifted/floating feel a gradient + shadow
    // would create.
    borderTop: "1px solid #E5E7EB",
    background: "#fff",
    padding: "16px 0 4px 0",
    marginTop: 18,
  },
  submitBtn: {
    width: "100%",
    padding: "13px 16px",
    borderRadius: 8,
    border: "none",
    background: "#E93D67",
    color: "#fff",
    fontSize: 15,
    fontWeight: 700,
    fontFamily: "inherit",
    cursor: "pointer",
    letterSpacing: 0.2,
  },
  // Variant of submitBtn used when paired with the "options" button so the
  // primary action stretches to fill remaining horizontal space.
  submitBtnFlex: { flex: 1, width: "auto" },
  submitRow: {
    display: "flex",
    gap: 8,
    alignItems: "stretch",
  },
  optionsBtn: {
    padding: "13px 14px",
    borderRadius: 8,
    border: "1px solid #E5E7EB",
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "inherit",
    height: "100%",
    whiteSpace: "nowrap",
  },
  // Positioned ABOVE the button row so it doesn't get clipped by the sticky
  // submit bar. `bottom: calc(100% + N)` is easier to reason about than top:
  // negative offsets across browsers.
  optionsMenu: {
    position: "absolute",
    bottom: "calc(100% + 6px)",
    insetInlineEnd: 0,
    minWidth: 240,
    background: "#fff",
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
    padding: 6,
    zIndex: 30,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  optionsItem: {
    background: "transparent",
    border: "none",
    color: "#111827",
    padding: "10px 12px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
    textAlign: "right",
  },
  previewPane: {
    background: "#F3F4F6",
    overflowY: "auto",
    padding: 32,
    fontFamily: "Heebo, Arial, sans-serif",
    position: "relative",
  },
  // Top banner — clearly states the right pane is a preview, not an issued doc.
  previewBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    background: "#FEF3C7",
    color: "#92400E",
    border: "1.5px solid #F59E0B",
    borderRadius: 10,
    padding: "10px 16px",
    fontSize: 14,
    fontWeight: 800,
    marginBottom: 20,
    maxWidth: 760,
    margin: "0 auto 20px",
    boxShadow: "0 2px 8px rgba(245, 158, 11, 0.15)",
  },
  previewBannerIcon: {
    fontSize: 18,
  },
  // Wrapper for the A4 preview + the diagonal watermark overlay.
  previewWatermarkWrap: {
    position: "relative",
  },
  // The actual "תצוגה מקדימה" diagonal text. Positioned absolutely over the
  // A4 card, pointer-events disabled so it doesn't block interactions.
  previewWatermarkInner: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
    zIndex: 5,
    overflow: "hidden",
  },
  previewWatermarkText: {
    fontSize: 120,
    fontWeight: 900,
    color: "rgba(233, 61, 103, 0.10)",
    letterSpacing: 8,
    transform: "rotate(-25deg)",
    whiteSpace: "nowrap",
    fontFamily: "Heebo, Arial, sans-serif",
    userSelect: "none",
  },
};

// Small shared field styles (inputs, sub-form layout)
const ss = {
  input: {
    width: "100%",
    boxSizing: "border-box",
    height: 36,
    border: "1.5px solid #E5E7EB",
    borderRadius: 8,
    padding: "0 10px",
    fontSize: 13,
    color: "#111827",
    background: "#fff",
    fontFamily: "inherit",
    outline: "none",
    direction: "rtl",
  },
  label: {
    display: "block",
    fontSize: 11,
    fontWeight: 600,
    color: "#6B7280",
    marginBottom: 4,
  },
  formRow: {
    marginBottom: 10,
  },
  formRowSplit: {
    display: "flex",
    gap: 8,
    marginBottom: 10,
  },
  subForm: {
    background: "#F9FAFB",
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    padding: 12,
    marginBottom: 12,
  },
  customForm: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: 8,
  },
  secondaryBtn: {
    padding: "10px 14px",
    border: "1.5px solid #E93D67",
    background: "#fff",
    color: "#E93D67",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 700,
    fontFamily: "inherit",
    cursor: "pointer",
  },
  catalogList: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    maxHeight: 240,
    overflowY: "auto",
    paddingInlineEnd: 4,
  },
  catalogPick: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid #E5E7EB",
    background: "#fff",
    fontFamily: "inherit",
    cursor: "pointer",
    textAlign: "right",
    transition: "background 250ms ease, border-color 250ms ease, transform 250ms ease",
  },
  catalogPickFlash: {
    background: "#DCFCE7",
    borderColor: "#22C55E",
    transform: "scale(1.02)",
  },
  catalogPickPlusFlash: {
    background: "#22C55E",
    color: "#fff",
  },
  catalogPickInfo: { flex: 1, minWidth: 0 },
  catalogPickName: {
    fontSize: 14,
    fontWeight: 600,
    color: "#111827",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  catalogPickDesc: {
    fontSize: 12,
    color: "#9CA3AF",
    marginTop: 2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  catalogPickPrice: {
    fontSize: 13,
    fontWeight: 700,
    color: "#374151",
  },
  catalogPickPlus: {
    width: 24,
    height: 24,
    borderRadius: 6,
    background: "#FCE7F3",
    color: "#E93D67",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    fontSize: 16,
    flexShrink: 0,
  },
  empty: {
    padding: 16,
    textAlign: "center",
    color: "#9CA3AF",
    fontSize: 12,
  },
};

// PDF-faithful preview (right pane). White A4-aspect card on light-gray bg.
const pv = {
  page: {
    background: "#fff",
    maxWidth: 760,
    margin: "0 auto",
    minHeight: 1100,
    padding: "48px 56px",
    borderRadius: 8,
    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
    direction: "rtl",
    fontFamily: "Heebo, Arial, sans-serif",
    color: "#111827",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 24,
    marginBottom: 12,
  },
  bizName: {
    fontSize: 18,
    fontWeight: 800,
    color: "#111827",
    marginBottom: 4,
  },
  bizMeta: {
    fontSize: 11,
    color: "#374151",
    marginTop: 1,
  },
  docTitle: {
    fontSize: 20,
    fontWeight: 800,
    color: "#111827",
    marginBottom: 6,
  },
  docMeta: {
    fontSize: 11,
    color: "#6B7280",
    marginTop: 2,
  },
  hr: {
    height: 1,
    background: "#E5E7EB",
    margin: "16px 0",
  },
  fieldLabel: {
    fontSize: 11,
    color: "#6B7280",
    marginBottom: 4,
    fontWeight: 600,
  },
  customerName: {
    fontSize: 14,
    fontWeight: 700,
    color: "#111827",
  },
  customerMeta: {
    fontSize: 12,
    color: "#374151",
    marginTop: 2,
  },
  placeholder: {
    color: "#9CA3AF",
    fontWeight: 400,
    fontStyle: "italic",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
    color: "#111827",
  },
  tableHead: { background: "#F3F4F6" },
  th: {
    textAlign: "right",
    padding: "8px 10px",
    fontWeight: 700,
    color: "#374151",
    borderBottom: "1px solid #E5E7EB",
    fontSize: 11,
  },
  tableRow: {},
  td: {
    padding: "8px 10px",
    borderBottom: "1px solid #F3F4F6",
    textAlign: "right",
  },
  tableEmpty: {
    padding: 20,
    textAlign: "center",
    color: "#9CA3AF",
    fontStyle: "italic",
    fontSize: 12,
  },
  totalsBlock: {
    background: "#F9FAFB",
    borderRadius: 8,
    padding: 14,
    display: "flex",
    flexDirection: "column",
    gap: 6,
    maxWidth: 320,
    marginInlineStart: "auto",
  },
  totalLine: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 12,
    color: "#374151",
  },
  totalGrand: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 15,
    fontWeight: 800,
    color: "#111827",
    paddingTop: 8,
    marginTop: 4,
    borderTop: "1px solid #E5E7EB",
  },
  noteText: {
    fontSize: 12,
    color: "#374151",
    fontStyle: "italic",
  },
  footer: {
    marginTop: 32,
    fontSize: 10,
    color: "#9CA3AF",
    textAlign: "center",
    fontStyle: "italic",
  },
};
